import { director, Director, Mat4, Node, Vec2, Vec3 } from 'cc';
import { EDITOR } from 'cc/env';
import Game from '../../../../Game';
import OrientationManager from '../../../../OrientationManager';
import SplineInterpolator, { INVALID_STEP, RunTimeSamples } from './SplineInterpolator';

// RunPath 被回收後移除它的 RESIZE 監聽 (見 RunPath._listenResize)
const resizeListenerRegistry =
    typeof FinalizationRegistry === 'undefined' ? null : new FinalizationRegistry<{ remove(): void }>((listener) => listener.remove());

// 取點結果 [x, y] (避免每次取點建立暫存物件; 以 typed array 保存, 寫入浮點數時不會配置記憶體)
const result = new Float64Array(2);

export default class RunPath {
    public static readonly SAMPLE_RATE = 1; // 取樣點倍率

    /* 匯入資料(計算用) */
    public transformToWorldPosition: boolean = true;
    public duration: number; // 路徑總時間
    public controlPoints: Vec3[]; // 路徑控制點
    public segmentLengths: number[]; // 各分段的長度

    /* 後續計算出的資料 */
    public totalSampleNumber: number; // 路徑總取樣數 (duration)
    public sampleNumber: number[]; // 各分段的取樣數
    // runTimeAngles: number[]; // 角度變化

    // 路徑點不再逐點保存成 Vec2, 只保存取樣資訊, 需要時以相同算式即時算出 (結果與原本保存的座標完全一致)
    private _samples: RunTimeSamples = null;
    private _curvePoints: Float64Array = null; // 各分段 4 個控制點的座標 (p0.x, p0.y, p1.x, p1.y, ..., p3.y)
    private _endPoints: Float64Array = null; // 第一個與最後一個控制點的座標
    private _segmentHint: number = 0; // 上次取點所在的分段 (逐幀取點多半落在同一分段)
    // 轉換成世界座標所用的 Canvas 世界矩陣, 與原本重新計算路徑點的時機相同 (初始化 / RESIZE) 時取得
    private _worldMatrix: Mat4 = null;
    // Vec3.transformMat4 用到的矩陣項, 依其運算順序排列; z 恆為 0, 含 z 的項預先乘好
    private _matrixTerms: Float64Array = null;
    private _hasCanvas: boolean = false;
    private _worldVersion: number = 0;
    private _alignPending: boolean = false;
    private _runTimePoints: Vec2[] = null;
    private _prefixMaxX: Float64Array = null;

    constructor(transformToWorldPosition: boolean = true) {
        // this.runTimeAngles = [];
        this.transformToWorldPosition = transformToWorldPosition;
        this.sampleNumber = [];
        this.totalSampleNumber = 0;
        this.controlPoints = [];
        this.segmentLengths = [];
    }

    /** 路徑點數量 */
    public get sampleCount(): number {
        return this._samples ? this._samples.count : 0;
    }

    /** 世界座標所用 Canvas 矩陣的版本, 矩陣改變時遞增 (依路徑點算出的資料可據此判斷是否需要重算) */
    public get worldVersion(): number {
        return this._worldVersion;
    }

    /**
     * 路徑點陣列, 僅為相容保留: 第一次取用時才建立並快取, 會佔用與舊版相同的記憶體
     * 逐幀取點請改用 sampleCount / getPoint
     */
    public get runTimePoints(): Vec2[] {
        if (!this._runTimePoints) {
            const points: Vec2[] = [];
            for (let i = 0; i < this.sampleCount; i++) {
                this._localPoint(i);
                this._toOutputSpace();
                points.push(new Vec2(result[0], result[1]));
            }
            this._runTimePoints = points;
        }
        return this._runTimePoints;
    }

    /**
     * 註冊 RESIZE 時更新世界矩陣 (註冊時機與順序與原本相同)
     * 監聽只弱引用 RunPath: 不再被使用的 RunPath 可被回收 (原本會被監聽永久持有), 監聽也隨之移除;
     * 不支援 WeakRef 的環境維持原本的強引用
     */
    private static _listenResize(runPath: RunPath) {
        const { orientation } = Game;
        if (!orientation) {
            return;
        }
        if (typeof WeakRef === 'undefined') {
            orientation.on(OrientationManager.EVENT_TYPE.RESIZE, runPath._onResize, runPath);
            return;
        }
        const target = new WeakRef(runPath);
        const listener = {
            onResize() {
                const current = target.deref();
                if (current) {
                    current._onResize();
                } else {
                    listener.remove();
                }
            },
            remove() {
                orientation.off(OrientationManager.EVENT_TYPE.RESIZE, listener.onResize, listener);
            },
        };
        orientation.on(OrientationManager.EVENT_TYPE.RESIZE, listener.onResize, listener);
        resizeListenerRegistry?.register(runPath, listener);
    }

    private static _sameTerms(a: Float64Array, b: Float64Array): boolean {
        if (a === b) {
            return true;
        }
        if (!a || !b) {
            return false;
        }
        for (let i = 0; i < a.length; i++) {
            if (!Object.is(a[i], b[i])) {
                return false;
            }
        }
        return true;
    }

    // 開始計算runTimePath 插值點
    public initRunTimeData(pathPercents: number[]): boolean {
        // 若控制點少於4點，則跳過
        if (this.controlPoints.length < 4) {
            return false;
        }
        // 總取樣數 = duration (預設speed=1情況下=總長度)
        this.totalSampleNumber = this.duration * RunPath.SAMPLE_RATE;
        // 在RunTime的時候，根據SegmentLength計算實際SampleNumber
        this.sampleNumber = SplineInterpolator.calculateRunTimeSampleNumber(pathPercents, this.totalSampleNumber);
        // 加入路徑點
        this._samples = SplineInterpolator.calculateRunTimeSamples(this.controlPoints, this.totalSampleNumber, this.segmentLengths, this.sampleNumber);
        this._storeControlPoints();
        this._updateRunTimePoints(true);
        if (this.transformToWorldPosition && !EDITOR) {
            RunPath._listenResize(this);
        }
        // 加入角度變化
        // this.runTimeAngles = SplineInterpolator.calculateRunTimeAngles(this.runTimePoints);
        return true;
    }

    /**
     * 取得第 index 個路徑點, 等同舊版的 runTimePoints[index].toVec3()
     * @param out 未提供時建立新的 Vec3
     */
    public getPoint(index: number, out: Vec3 = new Vec3()): Vec3 {
        if (!(index >= 0 && index < this.sampleCount) || (index | 0) !== index) {
            throw new RangeError(`RunPath.getPoint: index ${index} out of range (0 ~ ${this.sampleCount - 1})`);
        }
        this._localPoint(index);
        this._toOutputSpace();
        return out.set(result[0], result[1], 0);
    }

    /**
     * 平滑移動用: 取得第 frame 個 (可為小數) 路徑點
     * 曲線上的點不經係數表量化, 因此與 getPoint 會有些微差異 (起點與終點相同)
     * 超出頭尾的 frame 取頭尾的點 (小數幀加減 1 等運算可能略為超出, 例如反向時取 frame + 1)
     * @param out 未提供時建立新的 Vec3
     */
    public getPointAt(frame: number, out: Vec3 = new Vec3()): Vec3 {
        if (Number.isNaN(frame) || this.sampleCount === 0) {
            throw new RangeError(`RunPath.getPointAt: frame ${frame} out of range (0 ~ ${this.sampleCount - 1})`);
        }
        this._smoothLocalPoint(Math.max(0, Math.min(this.sampleCount - 1, frame)));
        this._toOutputSpace();
        return out.set(result[0], result[1], 0);
    }

    /**
     * 第一個 x >= targetX 的路徑點索引, 找不到時回傳 -1
     * 等同 runTimePoints.findIndex((point) => point.x >= targetX), 以前綴最大值二分搜尋
     */
    public findIndexAtLeastX(targetX: number): number {
        if (!this._prefixMaxX) {
            const prefixMax = new Float64Array(this.sampleCount);
            let max = -Infinity;
            for (let i = 0; i < prefixMax.length; i++) {
                this._localPoint(i);
                this._toOutputSpace();
                if (result[0] > max) {
                    max = result[0];
                }
                prefixMax[i] = max;
            }
            this._prefixMaxX = prefixMax;
        }
        const prefixMax = this._prefixMaxX;
        if (!(prefixMax.length > 0 && prefixMax[prefixMax.length - 1] >= targetX)) {
            return -1;
        }
        let low = 0;
        let high = prefixMax.length - 1;
        while (low < high) {
            const mid = (low + high) >> 1;
            if (prefixMax[mid] >= targetX) {
                high = mid;
            } else {
                low = mid + 1;
            }
        }
        return low;
    }

    private _storeControlPoints() {
        const points = this.controlPoints;
        const segmentCount = this._samples.segmentIsLine.length;
        const curvePoints = new Float64Array(segmentCount * 8);
        for (let segment = 0; segment < segmentCount; segment++) {
            for (let k = 0; k < 4; k++) {
                const point = points[segment * 3 + k];
                curvePoints[segment * 8 + k * 2] = point.x;
                curvePoints[segment * 8 + k * 2 + 1] = point.y;
            }
        }
        const first = points[0];
        const last = points[points.length - 1];
        this._curvePoints = curvePoints;
        this._endPoints = Float64Array.of(first.x, first.y, last.x, last.y);
        this._segmentHint = 0;
    }

    /**
     * RESIZE: 立即更新一次 (與原本相同), 並在該幀 update 結束後再更新一次:
     * RESIZE 發出時 Canvas 可能尚未依 Widget 重新對齊 (Widget 在 Director.EVENT_AFTER_UPDATE 才對齊),
     * 只用當下的矩陣會讓路徑停留在舊的位置, 直到下一次 RESIZE
     */
    private _onResize() {
        this._updateRunTimePoints();
        if (!this._alignPending) {
            this._alignPending = true;
            director.once(Director.EVENT_AFTER_UPDATE, this._onCanvasAligned, this);
        }
    }

    private _onCanvasAligned() {
        this._alignPending = false;
        this._updateRunTimePoints();
    }

    /**
     * 取得 Canvas 當下的世界矩陣; 與原本不同時更新並遞增 worldVersion
     * @param force 初始化時一律更新
     */
    private _updateRunTimePoints(force: boolean = false) {
        if (!this.transformToWorldPosition) {
            if (force) {
                this._worldVersion++;
            }
            return;
        }
        // 與舊版 point.toWorld(Game.canvasNode) 相同: 非 Node 時座標為 0, 否則使用 Canvas 當下的世界矩陣
        const canvas = Game.canvasNode;
        const hasCanvas = canvas instanceof Node;
        let terms: Float64Array = null;
        if (hasCanvas) {
            this._worldMatrix ??= new Mat4();
            const m = canvas.transform.node.getWorldMatrix(this._worldMatrix);
            const z = 0;
            terms = Float64Array.of(m.m00, m.m04, m.m08 * z, m.m12, m.m01, m.m05, m.m09 * z, m.m13, m.m03, m.m07, m.m11 * z, m.m15);
        }
        if (!force && hasCanvas === this._hasCanvas && RunPath._sameTerms(terms, this._matrixTerms)) {
            return;
        }
        this._hasCanvas = hasCanvas;
        this._matrixTerms = terms;
        this._worldVersion++;
        this._runTimePoints = null;
        this._prefixMaxX = null;
    }

    // 第 index 個取樣點所在的分段 (取樣數為0的分段與下一段起點相同, 取最後一個符合的分段即為非空分段)
    private _segmentOf(index: number): number {
        const { segmentStart } = this._samples;
        const hint = this._segmentHint;
        if (index >= segmentStart[hint] && index < segmentStart[hint + 1]) {
            return hint;
        }
        let low = 0;
        let high = segmentStart.length - 2;
        while (low < high) {
            const mid = (low + high + 1) >> 1;
            if (segmentStart[mid] <= index) {
                low = mid;
            } else {
                high = mid - 1;
            }
        }
        this._segmentHint = low;
        return low;
    }

    /**
     * 第 index 個取樣點的原座標 (寫入 result[0] / result[1])
     * 與 SplineInterpolator.calculateRunTimePoints(...)[index] 完全相同, 包含 Vec2 建構時 NaN / -0 轉為 0 的行為
     */
    private _localPoint(index: number) {
        const samples = this._samples;
        let x: number;
        let y: number;
        if (samples.endsOnly || index === samples.count - 1) {
            const offset = samples.endsOnly && index === 0 ? 0 : 2;
            x = this._endPoints[offset];
            y = this._endPoints[offset + 1];
        } else {
            const segment = this._segmentOf(index);
            const c = this._curvePoints;
            const base = segment * 8;
            if (samples.segmentIsLine[segment]) {
                const scale: number = (index - samples.segmentStart[segment]) / samples.lineSampleNumber[segment];
                x = c[base] * (1 - scale) + c[base + 6] * scale;
                y = c[base + 1] * (1 - scale) + c[base + 7] * scale;
            } else {
                const step = samples.steps[index];
                if (step === INVALID_STEP) {
                    x = 0;
                    y = 0;
                } else {
                    x = SplineInterpolator.bezierAt(c[base], c[base + 2], c[base + 4], c[base + 6], step);
                    y = SplineInterpolator.bezierAt(c[base + 1], c[base + 3], c[base + 5], c[base + 7], step);
                }
            }
        }
        result[0] = x || 0;
        result[1] = y || 0;
    }

    /**
     * 平滑移動: 第 frame 個 (可為小數) 取樣點的原座標 (寫入 result[0] / result[1])
     * 曲線分段以未量化的曲線參數求值 (點落在曲線上, 不受係數表 1/SEG 量化影響), 同一分段內以參數內插;
     * 跨分段或遇到無效取樣點時以前後兩點直線內插. 起點與終點與 _localPoint 相同
     */
    private _smoothLocalPoint(frame: number) {
        const samples = this._samples;
        const last = samples.count - 1;
        if (samples.endsOnly || frame >= last) {
            this._localPoint(frame >= last ? last : Math.round(frame));
            return;
        }
        const index = Math.floor(frame);
        const u = frame - index;
        const segment = this._segmentOf(index);
        if (u === 0) {
            this._smoothSegmentPoint(index, segment, 0);
            return;
        }
        if (index + 1 < last && this._segmentOf(index + 1) === segment) {
            this._smoothSegmentPoint(index, segment, u);
            return;
        }
        this._smoothSegmentPoint(index, segment, 0);
        const fromX = result[0];
        const fromY = result[1];
        if (index + 1 >= last) {
            this._localPoint(last);
        } else {
            this._smoothSegmentPoint(index + 1, this._segmentOf(index + 1), 0);
        }
        result[0] = fromX + (result[0] - fromX) * u;
        result[1] = fromY + (result[1] - fromY) * u;
    }

    // 平滑移動: 分段內第 index 個取樣點往下一點前進 u (0~1, 下一點須在同一分段) 的位置 (寫入 result[0] / result[1])
    private _smoothSegmentPoint(index: number, segment: number, u: number) {
        const samples = this._samples;
        const c = this._curvePoints;
        const base = segment * 8;
        let x: number;
        let y: number;
        if (samples.segmentIsLine[segment]) {
            const scale = (index + u - samples.segmentStart[segment]) / samples.lineSampleNumber[segment];
            x = c[base] * (1 - scale) + c[base + 6] * scale;
            y = c[base + 1] * (1 - scale) + c[base + 7] * scale;
        } else if (samples.steps[index] === INVALID_STEP || (u > 0 && samples.steps[index + 1] === INVALID_STEP)) {
            this._localPoint(u < 0.5 ? index : index + 1);
            return;
        } else {
            const t0 = samples.params[index];
            const t = u > 0 ? t0 + (samples.params[index + 1] - t0) * u : t0;
            const w = 1 - t;
            const a = w * w * w;
            const b = 3 * w * w * t;
            const d = 3 * w * t * t;
            const e = t * t * t;
            x = a * c[base] + b * c[base + 2] + d * c[base + 4] + e * c[base + 6];
            y = a * c[base + 1] + b * c[base + 3] + d * c[base + 5] + e * c[base + 7];
        }
        result[0] = x || 0;
        result[1] = y || 0;
    }

    /**
     * 將 result[0] / result[1] 轉換成保存的座標空間 (世界座標或原座標)
     * 世界座標與舊版 point.toWorld(canvas).toVec2() 相同: Vec3.transformMat4 (z = 0) 後再經 Vec2.set
     */
    private _toOutputSpace() {
        if (!this.transformToWorldPosition) {
            return;
        }
        if (!this._hasCanvas) {
            result[0] = 0;
            result[1] = 0;
            return;
        }
        const m = this._matrixTerms;
        const x = result[0];
        const y = result[1];
        let rhw = m[8] * x + m[9] * y + m[10] + m[11];
        rhw = rhw ? Math.abs(1 / rhw) : 1;
        result[0] = (m[0] * x + m[1] * y + m[2] + m[3]) * rhw || 0;
        result[1] = (m[4] * x + m[5] * y + m[6] + m[7]) * rhw || 0;
    }
}
